import { LegendConfig } from '../components/charts/MapLegend';

// Map variable configurations for different data types
export interface MapVariableConfig {
  id: string;
  name: string;
  description: string;
  dataSource: string;
  legendConfig: LegendConfig;
  colorFunction: (value: number, quintileBreaks?: number[]) => string;
}

// Income map configuration
export const incomeMapConfig: MapVariableConfig = {
  id: 'income',
  name: 'Median Household Income',
  description: 'Median household income by census tract',
  dataSource: 'acs5_income',
  legendConfig: {
    title: 'Median Household Income',
    dataYear: '2023 Data',
    // 10-class green (low) to red (high)
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: '$',
    tractCount: 0, // Will be set dynamically
    referenceValue: { label: 'California Median', value: 95521 }
  },
  colorFunction: (income: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => income < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};



// Example configuration for housing costs (for future use)
export const housingCostConfig: MapVariableConfig = {
  id: 'housing_cost',
  name: 'Median Housing Cost',
  description: 'Median housing cost by census tract',
  dataSource: 'housing_data',
  legendConfig: {
    title: 'Median Housing Cost',
    dataYear: '2023 Data',
    colorScale: ['#2166ac', '#4393c3', '#92c5de', '#d1e5f0', '#f7f7f7', '#fddbc7', '#f4a582', '#d6604d', '#b2182b'],
    breaks: [0, 200000, 300000, 400000, 500000, 600000, 700000, 800000, 1000000],
    unit: '$',
    tractCount: 0,
    referenceValue: { label: 'California Median', value: 550000 }
  },
  colorFunction: (cost: number): string => {
    if (cost < 200000) return '#2166ac';
    if (cost < 300000) return '#4393c3';
    if (cost < 400000) return '#92c5de';
    if (cost < 500000) return '#d1e5f0';
    if (cost < 600000) return '#f7f7f7';
    if (cost < 700000) return '#fddbc7';
    if (cost < 800000) return '#f4a582';
    if (cost < 1000000) return '#d6604d';
    return '#b2182b';
  }
};

// EV adoption configuration
export const evAdoptionConfig: MapVariableConfig = {
  id: 'ev_adoption',
  name: 'Electric Vehicles per 100 People',
  description: 'Number of electric vehicles per 100 people by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Electric Vehicles per 100 People',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: '',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 2.1 }
  },
  colorFunction: (evs: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => evs < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// Vehicles per household configuration
export const vehiclesPerHouseholdConfig: MapVariableConfig = {
  id: 'vehicles_per_household',
  name: 'Vehicles per Household',
  description: 'Average number of vehicles per household by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Vehicles per Household',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: '',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 2.1 }
  },
  colorFunction: (vehicles: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => vehicles < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// Total chargers configuration
export const totalChargersConfig: MapVariableConfig = {
  id: 'total_chargers',
  name: 'Total EV Chargers',
  description: 'Total number of EV chargers by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Total EV Chargers',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: '',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 5.2 }
  },
  colorFunction: (chargers: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    
    // For integer breaks, find the category the value belongs to
    // Breaks are sorted integers: [0, 1, 2, 3, 5, 10, ...]
    // Each break represents the upper bound of a category
    const roundedChargers = Math.round(chargers);
    
    // Find the last break that is <= the value
    // This gives us the category index
    let idx = -1;
    for (let i = 0; i < decileBreaks.length; i++) {
      if (roundedChargers <= decileBreaks[i]) {
        idx = i;
        break;
      }
    }
    
    // If value is less than first break (shouldn't happen if 0 is included), use first color
    if (idx < 0) return palette[0];
    
    // If value is greater than all breaks, use the next category (or last palette color)
    if (roundedChargers > decileBreaks[decileBreaks.length - 1]) {
      return palette[Math.min(idx + 1, palette.length - 1)];
    }
    
    // Use the category index (clamped to palette length)
    return palette[Math.min(idx, palette.length - 1)];
  }
};

// Population density configuration
export const populationDensityConfig: MapVariableConfig = {
  id: 'population_density',
  name: 'Population Density',
  description: 'Population density in persons per square mile by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Population Density',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: ' per sq mi',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 2500 }
  },
  colorFunction: (density: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => density < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// Political lean configuration with deciles
export const politicalLeanConfig: MapVariableConfig = {
  id: 'political_lean',
  name: 'Political Lean',
  description: 'Democratic minus Republican voter percentage by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Political Lean',
    dataYear: '2023 Data',
    colorScale: ['#8B0000', '#DC143C', '#FF6347', '#FFA07A', '#FFE4E1', '#E6F3FF', '#87CEEB', '#4169E1', '#0000CD', '#000080'],
    breaks: [], // Will be calculated dynamically based on deciles
    unit: '', // No unit - will display as "D +15" or "R +2"
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 15 }
  },
  colorFunction: (lean: number, decileBreaks?: number[]): string => {
    // Default breaks if decileBreaks not provided (fallback)
    // Breaks are sorted ascending: most negative (Republican) to most positive (Democratic)
    const breaks = decileBreaks || [-50, -40, -30, -20, -10, 0, 10, 20, 30, 40, 50];
    
    // Colors array matching the breaks (10 colors for 10 intervals between 11 breaks)
    // Index 0 = most Republican, Index 9 = most Democratic
    const colors = [
      '#8B0000', // Most Republican - dark red (values < breaks[1])
      '#DC143C', // Very Republican - crimson (breaks[1] <= value < breaks[2])
      '#FF6347', // Strong Republican - tomato (breaks[2] <= value < breaks[3])
      '#FFA07A', // Lean Republican - light salmon (breaks[3] <= value < breaks[4])
      '#FFE4E1', // Slight Republican - misty rose (breaks[4] <= value < breaks[5])
      '#E6F3FF', // Slight Democratic - light blue (breaks[5] <= value < breaks[6])
      '#87CEEB', // Lean Democratic - sky blue (breaks[6] <= value < breaks[7])
      '#4169E1', // Strong Democratic - royal blue (breaks[7] <= value < breaks[8])
      '#0000CD', // Very Democratic - medium blue (breaks[8] <= value < breaks[9])
      '#000080'  // Most Democratic - navy blue (breaks[9] <= value)
    ];
    
    // Handle values less than the minimum break (most Republican)
    if (lean < breaks[0]) {
      return colors[0];
    }
    
    // Find the interval the value falls into
    // For each interval [breaks[i], breaks[i+1]), return colors[i]
    for (let i = 0; i < breaks.length - 1; i++) {
      if (lean >= breaks[i] && lean < breaks[i + 1]) {
        return colors[i];
      }
    }
    
    // If value is >= last break, return the last (most Democratic) color
    return colors[colors.length - 1];
  }
};

// Carbon footprint configuration
export const carbonFootprintConfig: MapVariableConfig = {
  id: 'carbon_footprint',
  name: 'Average Household Carbon Footprint',
  description: 'Average household carbon footprint by census tract',
  dataSource: 'cbei_tracts',
  legendConfig: {
    title: 'Average Household Carbon Footprint',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: ' MT CO₂e/HH',
    tractCount: 0,
    referenceValue: { label: 'CA Average', value: 45.2 }
  },
  colorFunction: (footprint: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => footprint < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// Commute time configuration
export const commuteTimeConfig: MapVariableConfig = {
  id: 'commute_time',
  name: 'Average Commute Time',
  description: 'Average travel time to work in minutes by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Average Commute Time',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: ' min',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 28.5 }
  },
  colorFunction: (time: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => time < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// Zero vehicle households configuration
export const zeroVehicleHouseholdsConfig: MapVariableConfig = {
  id: 'zero_vehicle_households',
  name: 'Zero Vehicle Households',
  description: 'Number of households with no vehicles by census tract (weighted by population)',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Zero Vehicle Households',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: ' households',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 150 }
  },
  colorFunction: (count: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    
    // For integer breaks, find the category the value belongs to
    // Breaks are sorted integers: [0, 1, 2, 3, 5, 10, ...]
    // Each break represents the upper bound of a category
    const roundedCount = Math.round(count);
    
    // Find the last break that is <= the value
    // This gives us the category index
    let idx = -1;
    for (let i = 0; i < decileBreaks.length; i++) {
      if (roundedCount <= decileBreaks[i]) {
        idx = i;
        break;
      }
    }
    
    // If value is less than first break (shouldn't happen if 0 is included), use first color
    if (idx < 0) return palette[0];
    
    // If value is greater than all breaks, use the next category (or last palette color)
    if (roundedCount > decileBreaks[decileBreaks.length - 1]) {
      return palette[Math.min(idx + 1, palette.length - 1)];
    }
    
    // Use the category index (clamped to palette length)
    return palette[Math.min(idx, palette.length - 1)];
  }
};

// Owner-occupied housing configuration
export const ownerOccupiedHousingConfig: MapVariableConfig = {
  id: 'owner_occupied_housing',
  name: 'Owner-Occupied Housing',
  description: 'Percentage of owner-occupied housing units by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Owner-Occupied Housing',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: '%',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 55.3 }
  },
  colorFunction: (percentage: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => percentage < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// Educational attainment configuration
export const educationalAttainmentConfig: MapVariableConfig = {
  id: 'educational_attainment',
  name: 'Bachelor\'s Degree or Higher',
  description: 'Percentage of adults 25+ with bachelor\'s degree or higher by census tract',
  dataSource: 'ev_by_tract_2023',
  legendConfig: {
    title: 'Bachelor\'s Degree or Higher',
    dataYear: '2023 Data',
    colorScale: ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'],
    breaks: [],
    unit: '%',
    tractCount: 0,
    referenceValue: { label: 'California Average', value: 32.4 }
  },
  colorFunction: (percentage: number, decileBreaks?: number[]): string => {
    const palette = ['#1a9850', '#66bd63', '#a6d96a', '#d9ef8b', '#fee08b', '#fdae61', '#f46d43', '#d73027', '#b2182b', '#7f0000'];
    if (!decileBreaks || decileBreaks.length === 0) return palette[0];
    const idx = decileBreaks.findIndex((b) => percentage < b);
    return palette[idx >= 0 ? idx : palette.length - 1];
  }
};

// All available map configurations (in alphabetical order)
// Note: politicalLeanConfig is only available on the People page, not in the Maps page dropdown
// Note: carbonFootprintConfig and ownerOccupiedHousingConfig are only available on the Buildings (Energy) page
export const mapConfigs: MapVariableConfig[] = [
  // carbonFootprintConfig - moved to Buildings page only
  commuteTimeConfig,
  evAdoptionConfig,
  incomeMapConfig,
  // ownerOccupiedHousingConfig - moved to Buildings page only
  // politicalLeanConfig - moved to People page only
  populationDensityConfig,
  totalChargersConfig,
  vehiclesPerHouseholdConfig,
  zeroVehicleHouseholdsConfig
  // Note: housingCostConfig removed - no data available
];

// Note: politicalLeanConfig is already exported above and is available for use on People page
// It's excluded from mapConfigs array above so it won't appear in Maps page dropdown

// Helper function to get config by ID
export const getMapConfig = (id: string): MapVariableConfig | undefined => {
  // Check mapConfigs array first
  const configFromArray = mapConfigs.find(config => config.id === id);
  if (configFromArray) return configFromArray;
  
  // Check for configs that are exported separately (not in mapConfigs array)
  if (id === 'political_lean') {
    return politicalLeanConfig;
  }
  if (id === 'carbon_footprint') {
    return carbonFootprintConfig;
  }
  if (id === 'owner_occupied_housing') {
    return ownerOccupiedHousingConfig;
  }
  if (id === 'educational_attainment') {
    return educationalAttainmentConfig;
  }
  
  return undefined;
};
